import React, { useEffect, useRef, useState } from "react";
import explanationIcon from "../../assets/MentalPlus/ic_quefaire.svg";
import saveIcon from "../../assets/MentalPlus/ic_bookmark_off.svg";
import saveFilledIcon from "../../assets/MentalPlus/ic_bookmark_on.svg";
import shareIcon from "../../assets/MentalPlus/ic_share.svg";
import soundOnIcon from "../../assets/MentalPlus/ic_sound_on.svg";
import soundOffIcon from "../../assets/MentalPlus/ic_sound_off.svg";
import moreIcon from "../../assets/MentalPlus/ic_more.svg";
import styled from "styled-components";
import { useHlsPlayer } from "../../hooks/useHlsPlayer";

type Props = {
  src?: string;
  thumbnail?: string;
  active: boolean;
  muted: boolean;
  shouldPlay: boolean;
  liked: boolean;
  shared: boolean;
  saved: boolean;
  showExplication: boolean;
  onToggleLike(): void;
  onToggleSave(): void;
  onShare(): void;
  onToggleMute(): void;
  onShowExplication(): void;
  onMore?(): void;
};

const formatTime = (time: number) => {
  if (!isFinite(time) || time < 0) return "0:00";

  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);

  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

export const VideoSlide: React.FC<Props> = ({
  src,
  thumbnail,
  active,
  muted,
  shouldPlay,
  saved,
  showExplication,
  onShare,
  onToggleMute,
  onShowExplication,
  onToggleSave,
  onMore,
}) => {
  const { videoRef, hasFrame } = useHlsPlayer({
    src,
    active,
    muted,
    shouldPlay,
  });

  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  const [isSeeking, setIsSeeking] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  // Lecture x2 par appui long (maintenir) — comme sur les reels.
  const [speed2x, setSpeed2x] = useState(false);
  const longPressTimerRef = useRef<number | null>(null);
  const longPressActiveRef = useRef(false);
  const suppressClickRef = useRef(false);
  const tapStartRef = useRef<{ x: number; y: number } | null>(null);

  const seekBarRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !active) return;

    let raf: number;
    let hasMeta = false;

    const update = () => {
      if (
        el &&
        hasMeta &&
        el.duration &&
        isFinite(el.duration) &&
        el.duration > 0 &&
        !isSeeking
      ) {
        const ratio = Math.min(1, Math.max(0, el.currentTime / el.duration));
        setProgress(ratio);
        setCurrentTime(el.currentTime);
        setDuration(el.duration);
      }

      raf = requestAnimationFrame(update);
    };

    const onMeta = () => {
      hasMeta = true;
      setDuration(el.duration || 0);
      update();
    };

    el.addEventListener("loadedmetadata", onMeta);
    el.addEventListener("durationchange", onMeta);
    raf = requestAnimationFrame(update);

    return () => {
      el.removeEventListener("loadedmetadata", onMeta);
      el.removeEventListener("durationchange", onMeta);
      cancelAnimationFrame(raf);
    };
  }, [videoRef, active, isSeeking]);

  const handleTogglePlayPause = () => {
    const el = videoRef.current;
    if (!el) return;

    if (el.paused) {
      el.play().catch(() => {});
      setPaused(false);
    } else {
      el.pause();
      setPaused(true);
    }
  };

  /* ------------------------ Lecture x2 (appui long) ----------------------- */

  const startFastPlay = () => {
    longPressActiveRef.current = true;
    const el = videoRef.current;
    if (el) el.playbackRate = 2;
    setSpeed2x(true);
  };

  const stopFastPlay = () => {
    longPressActiveRef.current = false;
    const el = videoRef.current;
    if (el) el.playbackRate = 1;
    setSpeed2x(false);
  };

  const clearLongPressTimer = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleTapDown = (e: React.PointerEvent<HTMLDivElement>) => {
    tapStartRef.current = { x: e.clientX, y: e.clientY };
    longPressActiveRef.current = false;
    clearLongPressTimer();
    longPressTimerRef.current = window.setTimeout(startFastPlay, 250);
  };

  const handleTapMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const start = tapStartRef.current;
    if (!start || longPressActiveRef.current) return;
    const dx = Math.abs(e.clientX - start.x);
    const dy = Math.abs(e.clientY - start.y);
    // Un mouvement avant l'activation = swipe -> on annule l'appui long.
    if (dx > 10 || dy > 10) clearLongPressTimer();
  };

  const handleTapUp = () => {
    clearLongPressTimer();
    if (longPressActiveRef.current) {
      stopFastPlay();
      suppressClickRef.current = true; // évite le play/pause au relâchement
    }
    tapStartRef.current = null;
  };

  const handleTapClick = () => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    handleTogglePlayPause();
  };

  useEffect(() => {
    return () => clearLongPressTimer();
  }, []);

  const seekToClientX = (clientX: number) => {
    const bar = seekBarRef.current;
    const el = videoRef.current;
    if (!bar || !el || !el.duration || !isFinite(el.duration)) return;

    const rect = bar.getBoundingClientRect();
    const ratio = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
    const nextTime = ratio * el.duration;

    setProgress(ratio);
    setCurrentTime(nextTime);
    setDuration(el.duration);
    el.currentTime = nextTime;
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setIsSeeking(true);
    seekToClientX(e.clientX);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isSeeking) return;
    e.stopPropagation();
    seekToClientX(e.clientX);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    e.stopPropagation();
    seekToClientX(e.clientX);
    setIsSeeking(false);
  };

  return (
    <Slide>
      {thumbnail && (
        <Thumb
          src={thumbnail}
          alt=""
          style={{
            opacity: hasFrame ? 0 : 1,
            transitionDelay: hasFrame ? "0.1s" : "0s",
          }}
        />
      )}

      <VideoEl
        ref={videoRef}
        playsInline
        muted={muted}
        controls={false}
        loop
        style={{
          opacity: hasFrame ? 1 : 0,
          transitionDelay: hasFrame ? "0s" : "0s",
        }}
      />

      <TapCatcher
        onClick={handleTapClick}
        onPointerDown={handleTapDown}
        onPointerMove={handleTapMove}
        onPointerUp={handleTapUp}
        onPointerCancel={handleTapUp}
        onPointerLeave={handleTapUp}
      />

      {speed2x && <SpeedBadge>2x ▸▸</SpeedBadge>}

      <ActionsPanel>
        <ActionItem onClick={onToggleSave}>
          <IconImg src={saved ? saveFilledIcon : saveIcon} alt="save" />
        </ActionItem>

        <ActionItem onClick={onShare}>
          <IconImg src={shareIcon} alt="share" />
        </ActionItem>

        <ActionItem onClick={onToggleMute}>
          <IconImg src={muted ? soundOffIcon : soundOnIcon} alt="son" />
        </ActionItem>

        {showExplication && (
          <ActionItem onClick={onShowExplication}>
            <IconImg src={explanationIcon} alt="explication" />
          </ActionItem>
        )}

        <ActionItem onClick={onMore}>
          <IconImg src={moreIcon} alt="plus" />
        </ActionItem>
      </ActionsPanel>

      {isSeeking && (
        <TimeTooltip>
          {formatTime(currentTime)} / {formatTime(duration)}
        </TimeTooltip>
      )}

      <SeekBarWrapper
        ref={seekBarRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={() => setIsSeeking(false)}
      >
        <SeekTrack>
          <SeekFill $progress={progress} />
          <SeekThumb $progress={progress} />
        </SeekTrack>
      </SeekBarWrapper>
    </Slide>
  );
};

/* ---- styles ---- */

const Slide = styled.div`
  --seek-bottom-offset: 0px;
  position: relative;
  height: 100vh;
  height: 100dvh;
  width: 100vw;
  overflow: hidden;

  @media (max-width: 900px) and (orientation: portrait) {
    --seek-bottom-offset: calc(env(safe-area-inset-bottom, 0px) + 28px);
  }
`;

const Thumb = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: opacity 0.15s ease;

  @media (max-width: 900px) and (orientation: portrait) {
    object-position: center top;
  }
`;

const VideoEl = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #000;
  transition: opacity 0.15s ease;

  @media (max-width: 900px) and (orientation: portrait) {
    object-position: center top;
  }
`;

const TapCatcher = styled.div`
  position: absolute;
  inset: 0;
  cursor: pointer;
  z-index: 3;
`;

const SpeedBadge = styled.div`
  position: absolute;
  top: calc(env(safe-area-inset-top, 0px) + 70px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 8;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 14px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(6px);
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.5px;
  pointer-events: none;
`;

const ActionsPanel = styled.div`
  position: absolute;
  right: -2px;
  bottom: 90px;
  top: auto;
  transform: none;
  z-index: 6;
  width: 80px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 26px;
`;

const ActionItem = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  cursor: pointer;

  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;

  color: white;

  &:active {
    transform: scale(0.97);
  }
`;

const IconImg = styled.img`
  width: 32px;
  height: 32px;
  object-fit: contain;
  pointer-events: none;
  filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.5));
`;

const TimeTooltip = styled.div`
  position: absolute;
  bottom: calc(var(--seek-bottom-offset) + 15px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  background: rgba(0, 0, 0, 0.75);
  color: white;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 8px;
  border-radius: 999px;
  pointer-events: none;
`;

const SeekBarWrapper = styled.div`
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: var(--seek-bottom-offset);
  height: 22px;
  display: flex;
  align-items: center;
  z-index: 5;
  touch-action: none;
`;

const SeekTrack = styled.div`
  position: relative;
  width: 100%;
  height: 3px;
  background: rgba(255, 255, 255, 0.25);
  border-radius: 999px;
  overflow: visible;
`;

const SeekFill = styled.div<{ $progress: number }>`
  height: 100%;
  width: ${({ $progress }) => `${$progress * 100}%`};
  background: #ffffff;
  border-radius: 999px;
`;

const SeekThumb = styled.div<{ $progress: number }>`
  position: absolute;
  top: 50%;
  left: ${({ $progress }) => `${$progress * 100}%`};
  transform: translate(-50%, -50%);
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 0 6px rgba(0, 0, 0, 0.35);
`;